import { differenceInDays, differenceInHours, differenceInMinutes, format, parse } from 'date-fns'

export const DATA_FORMAT = 'dd/MM/yy'
export const TIME_FORMAT = 'h:mm aa'
// Export const DATA_FORMAT = 'yyyy-MM-dd';

export const dateToString = (date?: Date | string, dateFormat: string = DATA_FORMAT) => {
  try {
    return date ? format(typeof date === 'object' ? date : new Date(`${date}`), dateFormat) : ''
  } catch (error) {
    return typeof date === 'string' ? date : ''
  }
}

export const dateToStringApi = (date?: Date | string) =>
  date ? format(typeof date === 'object' ? date : new Date(`${date}`), 'dd-MM-yyyy') : '-'

export const dateToStringDateAndTime = (date?: Date | string) =>
  date ? format(new Date(date), `${DATA_FORMAT} ${TIME_FORMAT}`) : '-'

export const dateToStringTime = (date?: Date | string) =>
  date ? format(new Date(date), TIME_FORMAT) : '-'

export const dateToJSDate = (date: string) => {
  return parse(date, DATA_FORMAT, new Date())
}

// Show now or min ago, hours ago and yesterday and last would be the date
export const dateToStringAgo = (date: Date | string) => {
  const now = new Date()
  const minAgo = differenceInMinutes(now, new Date(date))
  const hoursAgo = differenceInHours(now, new Date(date))

  if (minAgo < 1) {
    return 'now'
  }

  if (minAgo < 60) {
    return `${minAgo}min ago`
  }

  if (hoursAgo < 24) {
    return `${hoursAgo}h ago`
  }

  if (hoursAgo < 48) {
    return 'yesterday'
  }

  return dateToString(date)
}

export const showDataAgo = (date: Date) => {
  const spacingBetweenDate = Math.abs(differenceInDays(date, new Date()))

  if (spacingBetweenDate > 1) {
    return `${spacingBetweenDate} days ago`
  }

  if (spacingBetweenDate > 0) {
    return 'today'
  }

  return dateToStringTime(date)
}

export function removeSeconds(time: null | string | undefined): string {
  if (!time) {
    return ''
  }

  const parts = time.split(':')
  if (parts.length < 2) {
    return time
  }

  return `${parts[0]}:${parts[1]}`
}

export const formatDateTime = (datetimeStr: Date | string) => {
  const date = new Date(datetimeStr)
  return format(date, `HH:mm MMM dd`)
}

export const getDayOfTheWeek = (date: Date): string => {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  return daysOfWeek[date.getDay()]
}

export const parseStringToDate = (date: string) => {
  const parsedDate = parse(date, DATA_FORMAT, new Date())

  return getUtcDate(parsedDate)
}

export const getUtcDate = (date: Date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
}

export function parseWeekAgo(time: string) {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

  return new Date(time) > sevenDaysAgo
    ? getDayOfTheWeek(new Date(time))
    : showDataAgo(new Date(time))
}

export const getClosestWorkingDate = (date: Date): Date => {
  const result = new Date(date.getTime())

  // Helper to check if a date is a weekend
  const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6

  // Helper to set time to 9 AM
  const setTo9AM = (d: Date) => {
    d.setHours(9, 0, 0, 0)
    return d
  }

  // Move to next weekday if on weekend
  while (isWeekend(result)) {
    result.setDate(result.getDate() + 1)
  }

  const hours = result.getHours()

  // Before 9 AM: Set to 9 AM today
  if (hours < 9) {
    return setTo9AM(result)
  }

  // After 6 PM: Move to 9 AM next weekday
  if (hours >= 18) {
    result.setDate(result.getDate() + 1)
    while (isWeekend(result)) {
      result.setDate(result.getDate() + 1)
    }
    return setTo9AM(result)
  }

  // Within business hours: Return as is
  return result
}
